/**
 * Portuguese (Português) Language File
 * 
 * This file contains all Portuguese translations for the application.
 */

export const pt = {
  // Language info
  language: {
    code: 'pt',
    name: 'Português',
    flag: '🇧🇷',
  },

  // Common/General translations
  common: {
    welcome: 'Bem-vindos',
    back: 'Voltar',
    backTo: 'Voltar para',
    next: 'Próximo',
    previous: 'Anterior',
    continue: 'Continuar',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    save: 'Salvar',
    delete: 'Excluir',
    edit: 'Editar',
    close: 'Fechar',
    loading: 'Carregando',
    error: 'Erro',
    success: 'Sucesso',
    warning: 'Aviso',
    info: 'Informação',
    yes: 'Sim',
    no: 'Não',
    ok: 'OK',
  },

  // Home Page
  home: {
    title: 'Bem-vindos',
    subtitle: 'Escolha um jogo para jogar:',
    chooseGame: 'Escolha um jogo',
    heroSubtitle: 'Jogos interativos que fortalecem a fé e conectam comunidades',
    exploreGames: 'Explorar Jogos',
    ourGames: 'Nossos Jogos',
    conectadosDescription: 'Explore categorias de Sabedoria e Aliança com perguntas projetadas para aprofundar seu conhecimento.',
    playGame: 'Jogar',
    followUs: 'Conecte-se conosco',
    copyright: '© 2025 Fe en Juego. Todos os direitos reservados.',
  },

  // Resources Section
  resources: {
    title: 'Recursos para Culto Jovem',
    subtitle: 'Materiais e recursos para download para líderes e equipes',
    download: 'Baixar',
    downloadLabel: 'Baixar',
    comingSoon: 'Em breve',

    // Placeholder resources
    placeholders: {
      guides: {
        title: 'Guias do Jogo',
        description: 'Instruções completas e guias passo a passo para facilitar o jogo em seu grupo.',
      },
      presentations: {
        title: 'Apresentações PPT',
        description: 'Apresentações prontas para usar em suas reuniões e atividades em grupo.',
      },
      additionalMaterial: {
        title: 'Material Adicional',
        description: 'PDFs, planilhas e outros recursos complementares para enriquecer a experiência.',
      },
    },
  },

  // Links of Interest Section
  links: {
    title: 'Links de Interesse',
    subtitle: 'Recursos e sites úteis para a comunidade adventista',
    visit: 'Visitar',
    visitLabel: 'Visitar site',

    // Individual links
    items: {
      iasd: {
        title: 'Página IASD',
        description: 'Site oficial da Igreja Adventista do Sétimo Dia',
      },
      noticiasIasd: {
        title: 'Notícias IASD',
        description: 'Portal de notícias adventistas',
      },
      whatsappUu: {
        title: 'Canal WhatsApp UU',
        description: 'Canal oficial do WhatsApp União Uruguaia',
      },
      feliz7play: {
        title: 'Feliz7Play',
        description: 'Plataforma de conteúdo audiovisual adventista',
      },
      videosIasd: {
        title: 'Vídeos IASD',
        description: 'Canal oficial do YouTube da IASD América do Sul',
      },
      radioNt: {
        title: 'Nuevo Tiempo UY',
        description: 'Rádio Nuevo Tiempo Uruguai ao vivo',
      },
      materialesIasd: {
        title: 'Materiais IASD',
        description: 'Centro de downloads de materiais adventistas',
      },
      paraElSabado: {
        title: 'Para o Sábado',
        description: 'Materiais especiais para suas atividades sabáticas',
      },
      galeriaImagenes: {
        title: 'Galeria de Imagens',
        description: 'Galeria de imagens cristãs para uso ministerial',
      },
      leccionJovenes: {
        title: 'Lição Jovens',
        description: 'Lições e materiais para classes de jovens',
      },
      jaMateriales: {
        title: 'Materiais JA',
        description: 'Materiais e informações para Jovens Adventistas',
      },
      accionJoven: {
        title: 'Revista Ação Jovem',
        description: 'Revista trimestral para jovens adventistas',
      },
      conquistadores: {
        title: 'Desbravadores',
        description: 'Materiais e informações do clube de Desbravadores',
      },
      ministerioNino: {
        title: 'Ministério da Criança',
        description: 'Recursos para o ministério infantil',
      },
      recursosBiblicos: {
        title: 'Recursos Bíblicos',
        description: 'Materiais e recursos para o estudo da Bíblia',
      },
      meditaciones2025: {
        title: 'Meditações 2025',
        description: 'Meditações para o pôr do sol 2025',
      },
      seme: {
        title: '7me',
        description: 'Aplicativo oficial para membros e amigos Adventistas',
      },
      probadVed: {
        title: 'Provai e Vede',
        description: 'Vídeos sobre mordomia cristã',
      },
    },
  },

  // Conecta2 Game
  conectados: {
    // Main page
    title: 'Conecta2',
    playing: 'Você está jogando',
    subtitle: 'Jogo de perguntas e respostas',
    selectCategory: 'Selecione uma categoria',

    // Access control
    access: {
      title: 'Acesso a Conecta2',
      message: 'Insira o código que acompanha seu jogo.',
      placeholder: 'XXXXXX',
      submit: 'Acessar',
      invalidCode: 'Código de acesso incorreto. Por favor, verifique o código nas instruções do jogo.',
      emptyCode: 'Por favor, insira um código de acesso.',
      success: 'Acesso concedido! Redirecionando...',
      infoTitle: 'Onde encontro o código?',
      infoText: 'O código de acesso está localizado nas instruções incluídas nas instruções do jogo.',
    },

    // Game controls
    startGame: 'Iniciar Jogo',
    endGame: 'Terminar Jogo',
    resetGame: 'Reiniciar Jogo',
    newGame: 'Novo Jogo',

    // Categories
    categories: {
      sabiduria: {
        title: 'Sabedoria',
        description: 'Teste seu conhecimento em diferentes áreas',
        alt: 'Sabedoria - Teste seu conhecimento de lugares, variedades e personagens',
      },
      alianza: {
        title: 'Aliança',
        description: 'Explore o pacto, a unidade e a coragem',
        alt: 'Aliança - Explore temas de pacto, unidade e coragem',
      },
    },

    // Subcategories
    subcategories: {
      // Sabiduria
      lugares: {
        title: 'Lugares',
        description: 'Lugares e localizações bíblicas',
      },
      variedades: {
        title: 'Variedades',
        description: 'Diferentes aspectos da fé',
      },
      personajes: {
        title: 'Personagens',
        description: 'Personagens e figuras bíblicas',
      },

      // Alianza
      'si-nosotros': {
        title: 'Se Nós',
        description: 'Nosso papel no pacto de Deus',
      },
      coincidimos: {
        title: 'Concordamos',
        description: 'Encontrar pontos em comum na fé',
      },
      arriesgamos: {
        title: 'Arriscamos',
        description: 'Tomar riscos por nossas crenças',
      },
    },

    // Question page
    question: {
      title: 'Pergunta',
      question: 'Pergunta',
      prompt: 'Instrução',
      answer: 'Resposta',
      options: 'Opções de resposta:',
      whichVerse: 'Qual versículo você usaria?',
      reference: 'Referência bíblica:',
      showAnswer: 'Mostrar Resposta',
      hideAnswer: 'Ocultar Resposta',
      endTurn: 'Terminar Turno',
      nextQuestion: 'Próxima Pergunta',
      loading: 'Carregando pergunta...',
      noQuestions: 'Não há mais perguntas disponíveis',
      error: 'Erro ao carregar perguntas',
      tryAgain: 'Tentar Novamente',
      trueOption: 'Verdadeiro',
      falseOption: 'Falso',
    },

    // Stats
    stats: {
      questionsAnswered: 'Perguntas respondidas:',
      totalQuestions: 'Total de perguntas:',
      questionsRemaining: 'Perguntas restantes:',
      resetQuestions: 'Reiniciar Perguntas',
      categoryStats: 'Estatísticas desta subcategoria',
    },

    // Completion
    completion: {
      congratulations: 'Parabéns!',
      allQuestionsCompleted: 'Você respondeu todas as perguntas desta categoria!',
      questionsCompleted: 'Perguntas completadas:',
      returnToConectados: 'Voltar para Conecta2',
      returnToCategories: 'Voltar para Categorias',
    },

    // Footer
    footer: {
      quote: '"É melhor ser dois do que um, porque ambos podem ajudar-se mutuamente a alcançar o sucesso."',
      reference: 'Eclesiastes 4:9 (NVT)',
    },

    // PDF Modal (How to Play)
    pdfModal: {
      title: 'Como Jogar',
      mobileTitle: 'Ver Instruções PDF',
      mobileDescription: 'Para uma melhor experiência em dispositivos móveis, você pode abrir o PDF em uma nova aba ou baixá-lo.',
      openPDF: 'Abrir PDF',
      downloadPDF: 'Baixar PDF',
    },
  },

  // Error messages
  errors: {
    general: 'Ocorreu um erro',
    notFound: 'Não encontrado',
    categoryNotFound: 'Categoria não encontrada',
    subcategoryNotFound: 'Subcategoria não encontrada',
    questionsNotFound: 'Nenhuma pergunta encontrada',
    loadingFailed: 'Erro ao carregar',
    networkError: 'Erro de conexão',
  },

  // Success messages
  success: {
    saved: 'Salvo com sucesso',
    deleted: 'Excluído com sucesso',
    updated: 'Atualizado com sucesso',
  },

  // Time and dates
  time: {
    seconds: 'segundos',
    minutes: 'minutos',
    hours: 'horas',
    days: 'dias',
    weeks: 'semanas',
    months: 'meses',
    years: 'anos',
  },

  // Accessibility
  a11y: {
    menu: 'Menu',
    close: 'Fechar',
    open: 'Abrir',
    previous: 'Anterior',
    next: 'Próximo',
    loading: 'Carregando',
  },

  // Thank You / Payment Confirmation Page
  gracias: {
    // Success state
    success: {
      title: 'Obrigado pela sua compra!',
      message: 'Seu pagamento foi recebido corretamente. Envie-nos o comprovante pelo WhatsApp junto com:',
      info1: 'Nome completo',
      info2: 'Endereço',
      info3: 'Telefone',
      info4: 'Forma de entrega (retirada ou envio)',
      paymentCode: 'Código de pagamento:',
      whatsappButton: 'Enviar pelo WhatsApp',
    },

    // Pending state
    pending: {
      title: 'Seu pagamento está pendente',
      message: 'Mercado Pago está revisando sua transação.',
      submessage: 'Notificaremos você quando o pagamento for confirmado.',
      paymentCode: 'Código de transação:',
    },

    // Failure state
    failure: {
      title: 'O pagamento não pôde ser concluído',
      message: 'Houve um problema ao processar sua compra.',
      submessage: 'Você pode tentar novamente ou nos contatar pelo WhatsApp se precisar de ajuda.',
      retryButton: 'Tentar novamente',
      contactButton: 'Contatar pelo WhatsApp',
    },

    // Waiting state (no params)
    waiting: {
      title: 'Aguardando informações',
      message: 'Confirme seu pagamento por Mercado Pago.',
      submessage: 'Avisanos por WhatsApp quando se confirme o pagamento.',
    },
  },

  // Conecta2 Product Landing Page
  conecta2Product: {
    // SEO & Meta
    meta: {
      title: 'Conecta2 — Jogo de tabuleiro cristão | Fe en Juego',
      description: 'Conecta2: jogo de tabuleiro cristão uruguaio. Ideal para famílias, jovens e igrejas. Preço de lançamento e reservas pelo WhatsApp.',
    },

    // Hero Section
    hero: {
      launchBadge: 'Preço',
      title: 'Lançamento oficial do Conecta2!',
      subtitle: 'Jogo de tabuleiro cristão para famílias, jovens e igrejas.',
      imageAlt: 'Caixa Conecta2 sobre a mesa',
    },

    // Price Section
    price: {
      title: 'Preço',
      subtitle: 'Escolha seu método de pagamento preferido',
      mercadopago: {
        label: 'MercadoPago',
        price: 940,
        detail: 'Até 12 parcelas',
      },
      transferencia: {
        label: 'Transferência / Dinheiro',
        price: 850,
        originalPrice: 940,
        discount: '10% OFF',
        detail: 'Pagamento único',
      },
      selected: 'Selecionado',
      reserveButton: 'Reservar pelo WhatsApp',
      paymentInfoLink: 'Consultar métodos de pagamento e dados de conta pelo WhatsApp',
      whatsappMessage: {
        reserve: `Olá! Quero reservar o Conecta2.
Quantidade: 
Pagamento: {paymentMethod}
Nome: 
Endereço (se envio): 
Vocês têm unidades disponíveis?`,
        paymentInfo: `Olá! Gostaria de saber:
• Todos os métodos de pagamento disponíveis
• Dados da conta para transferência
• Informações sobre MercadoPago

Obrigado!`,
      },
    },

    // What's Included Section
    includes: {
      title: 'O que inclui o Conecta2?',
      items: [
        'Tabuleiro',
        'Peças para até 10 jogadores',
        'Talão "Conexão"',
        'Instruções',
        'Acesso online a perguntas e desafios',
      ],
    },

    // How to Play Section
    howToPlay: {
      title: 'Como jogar (em 3 passos)',
      steps: [
        'Escolha sua peça e coloque-a no início.',
        'O jogador escolhe um conectado e resolve a atividade.',
        'O primeiro que chegar à casa final e resolver, ganha.',
      ],
    },

    // Gallery Section
    gallery: {
      title: 'Galeria de fotos',
      images: [
        { alt: 'Jogo completo Conecta2' },
        { alt: 'Tabuleiro e caixa do Conecta2' },
        { alt: 'App com perguntas e desafios' },
        { alt: 'Família jogando Conecta2' },
        { alt: 'Detalhe das peças do jogo' },
        { alt: 'Talão de conexão' },
        { alt: 'Instruções do jogo' },
        { alt: 'Jogadores em ação' },
        { alt: 'Componentes do jogo' },
      ],
    },

    // FAQ Section
    faq: {
      title: 'Perguntas frequentes',
      items: [
        {
          question: 'Como pago?',
          answer: 'Coordenamos pelo WhatsApp. Aceitamos transferências, dinheiro e MercadoPago (até 12 parcelas).',
        },
        {
          question: 'Posso comprar no atacado?',
          answer: 'Sim — escreva-nos pelo WhatsApp para ver preços por quantidade.',
        },
        {
          question: 'Vocês fazem envios?',
          answer: 'Sim, enviamos para todo o país através de agências de encomenda.',
        },
        {
          question: 'Quanto custa o envio?',
          answer: 'O custo depende da localidade e do método de envio escolhido. 💵 O cliente paga o envio ao receber ou retirar o pacote.',
        },
        {
          question: 'Como é coordenado o envio?',
          answer: 'Após confirmar a compra, coordenamos tudo pelo WhatsApp (método de envio, agência, horários, etc.) para que chegue da forma mais rápida e conveniente possível.',
        },
      ],
    },

    // Testimonial Section
    testimonial: {
      title: 'Fe en Juego',
      text: 'Jogos que unem a fé e a família',
      quote: '"Lâmpada para os meus pés é a tua palavra..." — Salmo 119:105',
    },

    // Footer
    footer: {
      copyright: '© 2025 Fe en Juego. Todos os direitos reservados.',
    },
  },
};

export default pt;
